import { makeAutoObservable } from 'mobx'
import { AxiosResponse } from 'axios'
import { isResponseSuccessful } from '../utils/helper'
import { OrderSummaryDto, SubmitOrderRequestDto } from '../utils/types'
import { ITherapyService } from '../service/utils/types'
import { IOrderSummaryStore } from './utils/types'

class OrderSummaryStore implements IOrderSummaryStore {
    therapyService: ITherapyService
    private _orderSummary: OrderSummaryDto | null = null
    private _loadingOrderSummary: boolean = false
    private _orderSummaryError: string | null = null

    constructor(therapyService: ITherapyService) {
        makeAutoObservable(this)
        this.therapyService = therapyService
    }

    get getOrderSummary(): OrderSummaryDto | null {
        return this._orderSummary
    }

    get loadingOrderSummary(): boolean {
        return this._loadingOrderSummary
    }

    get orderSummaryError(): string | null {
        return this._orderSummaryError
    }

    async checkTherapyOrderSummary(therapyId: string): Promise<OrderSummaryDto> {
        this._loadingOrderSummary = true
        this._orderSummaryError = null

        try {
            const response: AxiosResponse<OrderSummaryDto> = await this.therapyService.checkTherapyOrderSummary(therapyId)
            if (!isResponseSuccessful(response)) {
                console.error(`Failed to get order summary for therapyId: ${therapyId}. Reason: ${response}`)
                this._orderSummaryError = 'Failed to get order summary'
                return Promise.reject()
            }

            this._orderSummary = response.data
            return Promise.resolve(response.data)
        } catch (error) {
            this._orderSummaryError = error instanceof Error ? error.message : 'Failed to fetch order summary'
            throw error
        } finally {
            this._loadingOrderSummary = false
        }
    }

    async submitTherapyOrder(request: SubmitOrderRequestDto): Promise<void> {
        const response: AxiosResponse<void> = await this.therapyService.submitTherapyOrder(request)
        if (!isResponseSuccessful(response)) {
            console.error(`Failed to submit therapy order for therapyId: ${request.therapy_id}. Reason: ${response}`)
            return Promise.reject()
        }

        // After successful submission, update the order summary
        await this.checkTherapyOrderSummary(request.therapy_id)
    }
}

export default OrderSummaryStore